import React from 'react';
import { ArrowRight, ArrowUpRight, Check, Upload, Cpu, Download } from 'lucide-react';
import { ProductSwitcher, MobileAppSwitcher } from './AppSwitcher';
import FluidBackground from './FluidBackground';
import Footer from './Footer';
import BeforeAfterCompare from './BeforeAfterCompare';
import LandingJsonLd from './LandingJsonLd';
import { HDR_CREDIT_COPY, HDR_PRICING_COPY } from '@rehub/pricing';
import { HdrPricingSlider, HdrWorkflowStrip } from '@rehub/pricing-ui';
import { AskAiSection, buildAskAiPrompt, buildAskAiProviders } from '@rehub/ask-ai';
import { trackHdrEvent } from '../services/analytics';
import {
  BENEFITS,
  CTA_SEE_RESULTS,
  CTA_TRY_FREE,
  FREE_OFFER_LINE,
  GALLERY_EXAMPLES,
  HERO_BRACKETS,
  HERO_COMPARE,
  HERO_POINTS,
  HOME_FAQS,
  LANDING_H1,
  LANDING_SUPPORTING,
  PROPERTY_SET,
  SISTER_APPS,
  WORKFLOW,
  WORKFLOW_EXAMPLE,
} from '../lib/landing';

interface LandingPageProps {
  onSignUp: () => void;
  onLogin: () => void;
}

const ASK_AI_PRODUCT = 'HDR Photo';
const ASK_AI_PROVIDERS = buildAskAiProviders(ASK_AI_PRODUCT);
const ASK_AI_PROMPT = buildAskAiPrompt({
  productName: ASK_AI_PRODUCT,
  siteUrl: 'https://hdrphoto.rehub.studio',
  extraFocus:
    'Focus on automatic HDR bracket merging for property photos, window pull, alignment, natural colour, batch processing and monthly HDR Credit plans separate from ReHub Credits.',
});

const WORKFLOW_ICONS = [Upload, Cpu, Download];

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-brand-400">{children}</p>
);

const LandingPage: React.FC<LandingPageProps> = ({ onSignUp, onLogin }) => {
  const signUp = (placement: string) => {
    trackHdrEvent('hdr_landing_cta_clicked', { placement });
    onSignUp();
  };

  return (
    <div className="relative flex min-h-screen flex-col text-slate-100">
      <LandingJsonLd />
      <FluidBackground />

      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-slate-950/70 backdrop-blur-md">
        <div className="flex flex-col px-4 sm:px-6 md:flex-row md:items-center md:h-16">
          <div className="flex min-h-14 w-full items-center justify-between gap-3 py-3 md:min-h-0 md:py-0">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              <a href="/" className="group flex shrink-0 items-center">
                <img
                  src="/hdr-photo-logo.png"
                  alt="HDR Photo"
                  className="h-11 w-auto object-contain transition-opacity group-hover:opacity-90 sm:h-12"
                />
              </a>
              <ProductSwitcher currentApp="hdrphoto" />
            </div>
            <nav className="hidden items-center gap-6 text-sm text-slate-400 lg:flex" aria-label="Page">
              <a href="#results" className="transition-colors hover:text-white">Results</a>
              <a href="#how" className="transition-colors hover:text-white">How it works</a>
              <a href="#pricing" className="transition-colors hover:text-white">Pricing</a>
              <a href="#faq" className="transition-colors hover:text-white">FAQ</a>
            </nav>
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={onLogin}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-white sm:px-4"
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => signUp('nav')}
                className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand-900/30 transition-colors hover:bg-brand-500"
              >
                Try HDR Photo
                <ArrowRight size={15} />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 overflow-x-auto border-t border-slate-800 py-2.5 text-xs text-slate-400 lg:hidden">
            <a href="#results" className="shrink-0 hover:text-white">Results</a>
            <a href="#how" className="shrink-0 hover:text-white">How it works</a>
            <a href="#pricing" className="shrink-0 hover:text-white">Pricing</a>
            <a href="#faq" className="shrink-0 hover:text-white">FAQ</a>
          </div>
          <div className="flex items-center justify-center gap-3 overflow-x-auto border-t border-slate-800 py-2.5 md:hidden">
            <MobileAppSwitcher currentApp="hdrphoto" />
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1">
        <section className="container mx-auto px-4 pb-16 pt-12 sm:pt-16">
          <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2 lg:gap-12">
            <div>
              <p className="inline-flex rounded-full border border-brand-500/20 bg-brand-900/30 px-4 py-2 text-sm font-medium text-brand-300">
                Automatic HDR for property photography
              </p>
              <h1 className="mt-6 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.65rem] lg:leading-tight">
                {LANDING_H1}
              </h1>
              <p className="speakable-intro mt-5 max-w-xl text-lg leading-relaxed text-slate-300">
                {LANDING_SUPPORTING}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => signUp('hero')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-brand-600 px-6 py-3.5 text-base font-semibold text-white shadow-xl shadow-brand-900/40 transition-colors hover:bg-brand-500"
                >
                  {CTA_TRY_FREE}
                  <ArrowRight size={18} />
                </button>
                <a
                  href="#results"
                  className="inline-flex items-center rounded-2xl border-2 border-slate-700 bg-slate-900/70 px-6 py-3.5 text-base font-semibold text-slate-200 transition-colors hover:border-slate-500 hover:text-white"
                >
                  {CTA_SEE_RESULTS}
                </a>
              </div>
              <p className="mt-4 text-sm text-slate-400">{FREE_OFFER_LINE}</p>
              <ul className="mt-8 space-y-2">
                {HERO_POINTS.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <Check size={16} className="shrink-0 text-sky-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="min-w-0">
              <BeforeAfterCompare
                {...HERO_COMPARE}
                priority
                placement="hero"
              />
              <div className="mt-4 grid grid-cols-3 gap-2">
                {HERO_BRACKETS.map((frame) => (
                  <figure key={frame.label} className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
                    <img
                      src={frame.src}
                      alt={frame.alt}
                      width={1536}
                      height={1024}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[3/2] w-full object-cover"
                    />
                    <figcaption className="px-2 py-1.5 text-center text-[11px] font-medium text-slate-400">
                      {frame.label}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="results" className="container mx-auto scroll-mt-20 px-4 py-16">
          <div className="mx-auto max-w-3xl text-center">
            <SectionLabel>Results</SectionLabel>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Different HDR challenges</h2>
            <p className="mt-3 text-slate-400">
              Demonstration plates from matching interiors. These show the lighting problems HDR Photo is built to solve — not named client jobs.
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-6xl gap-8 lg:grid-cols-2">
            {GALLERY_EXAMPLES.map((example) => (
              <div key={example.id}>
                <h3 className="mb-3 text-lg font-semibold text-white">{example.title}</h3>
                <BeforeAfterCompare
                  beforeSrc={example.beforeSrc}
                  afterSrc={example.afterSrc}
                  beforeAlt={example.beforeAlt}
                  afterAlt={example.afterAlt}
                  beforeLabel={example.beforeLabel}
                  afterLabel={example.afterLabel}
                  width={1536}
                  height={1024}
                  caption={example.caption}
                  placement={example.id}
                />
              </div>
            ))}
          </div>
          <div className="mx-auto mt-12 max-w-6xl">
            <h3 className="text-lg font-semibold text-white">Consistency across a property set</h3>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              The same merge-and-enhance path runs across rooms so a listing set stays even, rather than grading every frame from scratch.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
              {PROPERTY_SET.map((item) => (
                <figure key={item.label} className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
                  <img
                    src={item.src}
                    alt={item.alt}
                    width={1536}
                    height={1024}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[3/2] w-full object-cover"
                  />
                  <figcaption className="px-3 py-2 text-sm text-slate-300">{item.label}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="how" className="container mx-auto scroll-mt-20 px-4 py-16">
          <div className="mx-auto max-w-3xl text-center">
            <SectionLabel>How it works</SectionLabel>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">From brackets to a publish-ready set</h2>
            <p className="mt-3 text-slate-400">Three steps. No manual blending, masking or tone-mapping.</p>
          </div>
          <div className="mx-auto mt-10 grid max-w-5xl gap-6 md:grid-cols-3">
            {WORKFLOW.map((step, index) => {
              const Icon = WORKFLOW_ICONS[index];
              return (
                <div key={step.title} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 backdrop-blur-md">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/15 text-brand-300">
                      <Icon size={22} />
                    </span>
                    <span className="text-4xl font-bold text-slate-800">{index + 1}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{step.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section id="features" className="container mx-auto scroll-mt-20 px-4 py-16">
          <div className="mx-auto max-w-3xl text-center">
            <SectionLabel>Why HDR Photo</SectionLabel>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Built for property photography</h2>
            <p className="mt-3 text-slate-400">
              Distinct parts of the workflow — not the same claim repeated.
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-6xl gap-5 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 backdrop-blur-md">
                <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="pricing" className="container mx-auto scroll-mt-20 px-4 py-16">
          <div className="mx-auto max-w-3xl text-center">
            <SectionLabel>Pricing</SectionLabel>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">{HDR_PRICING_COPY.heading}</h2>
            <p className="mt-3 text-slate-400">{HDR_PRICING_COPY.supporting}</p>
          </div>
          <div className="mx-auto mt-10 grid max-w-6xl items-start gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(16rem,0.8fr)]">
            <HdrPricingSlider
              onCta={() => signUp('pricing')}
              onView={(plan, currency) =>
                trackHdrEvent('hdr_pricing_viewed', {
                  plan: plan.id,
                  allowance: plan.imageAllowance,
                  currency,
                })
              }
              onPlanChange={(plan, currency) =>
                trackHdrEvent('hdr_pricing_slider_changed', {
                  plan: plan.id,
                  allowance: plan.imageAllowance,
                  currency,
                })
              }
            />
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 backdrop-blur-md">
              <h3 className="text-lg font-semibold text-white">What one HDR Credit buys</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                {HDR_CREDIT_COPY.optimisedHelper}
              </p>
              <ul className="mt-5 space-y-3 text-sm text-slate-300">
                <li className="flex gap-2.5">
                  <Check size={16} className="mt-0.5 shrink-0 text-sky-400" />
                  Charged per finished image, not per uploaded source file.
                </li>
                <li className="flex gap-2.5">
                  <Check size={16} className="mt-0.5 shrink-0 text-sky-400" />
                  HDR Credits are a separate monthly balance from ReHub Credits.
                </li>
                <li className="flex gap-2.5">
                  <Check size={16} className="mt-0.5 shrink-0 text-sky-400" />
                  Failed jobs are not charged. A repeat merge uses another HDR Credit.
                </li>
                <li className="flex gap-2.5">
                  <Check size={16} className="mt-0.5 shrink-0 text-sky-400" />
                  Taxes are calculated at checkout. Monthly unused credits do not roll into a larger balance.
                </li>
              </ul>
              <p className="mt-5 text-sm text-slate-500">{FREE_OFFER_LINE}</p>
            </div>
          </div>
          <div className="mt-12">
            <p className="mb-4 text-center text-sm font-medium text-slate-400">
              How HDR Photo starts the ReHub workflow
            </p>
            <HdrWorkflowStrip />
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <div className="mx-auto max-w-3xl rounded-3xl border border-slate-800 bg-slate-900/60 p-8 backdrop-blur-md sm:p-10">
            <SectionLabel>In practice</SectionLabel>
            <h2 className="text-3xl font-bold text-white">{WORKFLOW_EXAMPLE.title}</h2>
            <p className="mt-4 leading-relaxed text-slate-300">{WORKFLOW_EXAMPLE.body}</p>
          </div>
        </section>

        <section id="faq" className="container mx-auto scroll-mt-20 px-4 py-16">
          <div className="mx-auto max-w-3xl">
            <div className="mb-10 text-center">
              <SectionLabel>FAQ</SectionLabel>
              <h2 className="text-3xl font-bold text-white sm:text-4xl">Frequently asked questions</h2>
            </div>
            <div className="space-y-3">
              {HOME_FAQS.map((faq) => (
                <details
                  key={faq.q}
                  className="group rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-md open:border-slate-700"
                >
                  <summary className="cursor-pointer list-none px-5 py-4 font-semibold text-slate-100 marker:content-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400">
                    <span className="flex items-center justify-between gap-4">
                      {faq.q}
                      <span className="text-slate-500 transition-transform group-open:rotate-180" aria-hidden>
                        ▾
                      </span>
                    </span>
                  </summary>
                  <p className="px-5 pb-5 text-sm leading-relaxed text-slate-400">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <div className="mx-auto max-w-6xl">
            <div className="max-w-2xl">
              <SectionLabel>ReHub Studio</SectionLabel>
              <h2 className="text-3xl font-bold text-white">After the photograph is finished</h2>
              <p className="mt-3 text-slate-400">
                HDR Photo uses the same ReHub login as the rest of the suite. Files are not transferred automatically, and HDR Credits stay separate from ReHub Credits used by these tools.
              </p>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {SISTER_APPS.map((app) => (
                <a
                  key={app.name}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col rounded-2xl border border-slate-800 bg-slate-900/60 p-5 transition-colors hover:border-slate-600"
                >
                  <img src={app.logo} alt="" className="mb-4 h-8 w-auto object-contain object-left" />
                  <p className="font-semibold text-white">{app.name}</p>
                  <p className="mt-1 flex-1 text-sm text-slate-400">{app.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-slate-200 group-hover:text-white">
                    Open
                    <ArrowUpRight size={14} className="text-slate-500 group-hover:text-sky-300" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <AskAiSection
          productName={ASK_AI_PRODUCT}
          prompt={ASK_AI_PROMPT}
          providers={ASK_AI_PROVIDERS}
          description="Ask ChatGPT, Perplexity, Grok, Claude or Gemini about HDR Photo’s bracket merging, window pull, pricing and whether it fits your property photography workflow."
        />

        <section className="container mx-auto px-4 pb-20">
          <div className="mx-auto max-w-4xl rounded-3xl border border-brand-500/30 bg-gradient-to-b from-brand-950/40 to-slate-900/60 p-10 text-center backdrop-blur-md sm:p-14">
            <p className="mb-5 text-sm font-semibold text-emerald-300">{FREE_OFFER_LINE}</p>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Start with your next property shoot</h2>
            <p className="mx-auto mt-4 mb-8 max-w-xl text-slate-300">
              Create an account, upload a folder and review the first merged JPEGs. HDR Photo uses the same ReHub login if you already have one.
            </p>
            <button
              type="button"
              onClick={() => signUp('final_cta')}
              className="inline-flex items-center gap-2 rounded-2xl bg-brand-600 px-8 py-4 text-lg font-semibold text-white shadow-xl shadow-brand-900/40 transition-colors hover:bg-brand-500"
            >
              {CTA_TRY_FREE}
              <ArrowRight size={18} />
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;
